import type { WrapBand } from '@/lib/text/types/wrap-band'
import type { FreeSegment } from './types/free-segment'

/** Below this width, a "free" segment is numerical noise from slice-based contour sampling, not
 *  real usable space — e.g. a deep, narrow notch between two star points can produce a computed
 *  gap a fraction of a pixel wide at the point where two adjacent bands almost (but don't quite)
 *  touch. No real character renders in under a pixel, so offering it as a layout target lets a
 *  character get placed there anyway, visibly overlapping the shape on both sides of the sliver
 *  (see `computeShapeContourBands` — this is independent of that slicing and applies to any
 *  source of a near-zero-width gap, not just contour shapes). */
const MIN_USABLE_FREE_SEGMENT_PX = 1

/**
 * Free horizontal segments for one row's `[rowTop, rowBottom)` Y-range, given every wrap band that
 * might overlap it. Merges overlapping/adjacent bands before complementing, so multiple bands over
 * the same row still produce correctly-merged exclusions. Pure geometry — no DOM.
 */
export function computeRowFreeSegments(
  bands: WrapBand[],
  rowTop: number,
  rowBottom: number,
  contentWidth: number
): FreeSegment[] {
  if (contentWidth <= 0) return []

  const overlapping = bands
    .filter((b) => b.top < rowBottom && b.bottom > rowTop)
    .map((b) => ({ start: Math.max(0, b.left), end: Math.min(contentWidth, b.right) }))
    .filter((iv) => iv.end > iv.start)
    .sort((a, b) => a.start - b.start)

  if (overlapping.length === 0) return [{ start: 0, end: contentWidth }]

  const merged: FreeSegment[] = []

  for (const iv of overlapping) {
    const last = merged[merged.length - 1]

    if (last && iv.start <= last.end) last.end = Math.max(last.end, iv.end)
    else merged.push({ ...iv })
  }

  const free: FreeSegment[] = []
  let cursor = 0

  for (const excluded of merged) {
    if (excluded.start > cursor) free.push({ start: cursor, end: excluded.start })
    cursor = Math.max(cursor, excluded.end)
  }

  if (cursor < contentWidth) free.push({ start: cursor, end: contentWidth })

  return free.filter((seg) => seg.end - seg.start >= MIN_USABLE_FREE_SEGMENT_PX)
}
